/**
 * Drill-down topic taxonomy (category › topic › subtopic). Paths are strings like
 * "sports/football/referees". A custom "Other" segment is "~Text".
 * Old flat topic ids map to paths via `legacy`, so existing profiles still match.
 */
import TAXONOMY from './topicTaxonomy.json';
// Two-way aliases (same subtopic under two topics) so both paths match.
const ALIASES = Object.entries(TAXONOMY.aliases || {}).reduce((m, [a, b]) => ({ ...m, [a]: b, [b]: a }), {});
export const aliasOf = p => ALIASES[p] || null;

export const CATEGORIES = TAXONOMY.categories;
export const JUST_TALK_PATH = 'just-talk';
export const SEP = ' › ';
export const BOOKING_PATH_LIMIT = 3;
export const PROFILE_PATH_LIMIT = 40;
export const OTHER_MAX = 40;
export const NOTE_MAX = 140;

const byPath = new Map();
const legacyToPath = {};
const walk = (nodes, prefix = '', ancestors = []) =>
  nodes.forEach(node => {
    const path = prefix ? `${prefix}/${node.id}` : node.id;
    const entry = { ...node, path, ancestors, depth: ancestors.length + 1 };
    byPath.set(path, entry);
    (node.legacy || []).forEach(id => {
      if (!legacyToPath[id]) legacyToPath[id] = path;
    });
    if (node.children) walk(node.children, path, [...ancestors, path]);
  });
walk(CATEGORIES);

export const LEGACY_TO_PATH = legacyToPath;
export const nodeFor = path => byPath.get(path) || null;

const segs = path => String(path || '').split('/').filter(Boolean);

export const isCustomSegment = seg => typeof seg === 'string' && seg.startsWith('~');

/** Children of a path ('' = categories). Unfeatured stay selectable (pickers) but sorted last. */
export const childrenOf = path => {
  if (!path) return CATEGORIES.map(c => nodeFor(c.id));
  const n = nodeFor(path);
  return n?.children ? n.children.map(c => nodeFor(`${path}/${c.id}`)) : [];
};

/** Labels for each level of a path. */
export const pathLabels = path => {
  const parts = segs(path);
  const out = [];
  let cur = '';
  for (const p of parts) {
    cur = cur ? `${cur}/${p}` : p;
    out.push(isCustomSegment(p) ? p.slice(1) : nodeFor(cur)?.label || p);
  }
  return out;
};
export const pathLabel = path => pathLabels(path).join(SEP);

/** Clean, validate (known nodes + at most one trailing custom segment). */
export const normalizePath = value => {
  const parts = segs(value);
  if (!parts.length) return null;
  let cur = '';
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (isCustomSegment(p)) {
      const text = p.slice(1).replace(/[\/\s]+/g, ' ').trim().slice(0, OTHER_MAX);
      return text && i === parts.length - 1 ? (cur ? `${cur}/~${text}` : `~${text}`) : cur || null;
    }
    const next = cur ? `${cur}/${p}` : p;
    if (!nodeFor(next)) return cur || null;
    cur = next;
  }
  return cur || null;
};

export const normalizePaths = (value, limit = PROFILE_PATH_LIMIT) => {
  const list = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : [];
  const seen = new Set();
  const out = [];
  list.forEach(v => {
    const p = normalizePath(v);
    if (p && !seen.has(p)) {
      seen.add(p);
      out.push(p);
    }
  });
  return out.slice(0, limit);
};

/** Same branch at any depth: equal, or one is an ancestor of the other. */
export const pathsRelated = (a, b) => a === b || a.startsWith(`${b}/`) || b.startsWith(`${a}/`);

/** Paths a listing is tagged with (new paths + old flat topics mapped). */
export const pathsFromListing = listing => {
  const pd = listing?.attributes?.publicData || listing?.publicData || {};
  const own = normalizePaths(pd.topicPaths);
  const legacy = (Array.isArray(pd.topics) ? pd.topics : [])
    .map(id => LEGACY_TO_PATH[id])
    .filter(Boolean);
  // No topics yet → 'Just need someone to talk to' by default (Sam, Oct 15).
  const base = own.length || legacy.length ? [...new Set([...own, ...legacy])] : [JUST_TALK_PATH];
  const extra = base.map(p => ALIASES[p]).filter(Boolean);
  return [...new Set([...base, ...extra])];
};

/** Browse: a listener matches if any selected path is on the same branch as any of theirs. */
export const listingMatchesPaths = (listing, selected = []) => {
  if (!selected.length) return true;
  const mine = pathsFromListing(listing);
  return selected.some(s => mine.some(m => pathsRelated(s, m)));
};

/** Deepest known ancestor-or-self with a legacy id → old flat topic id (keeps old search working). */
export const legacyTopicForPath = path => {
  let cur = normalizePath(path);
  while (cur) {
    const n = nodeFor(cur);
    if (n?.legacy?.length) return n.legacy[0];
    cur = cur.includes('/') ? cur.slice(0, cur.lastIndexOf('/')) : null;
  }
  return null;
};
export const legacyTopicsForPaths = paths =>
  [...new Set(normalizePaths(paths).map(legacyTopicForPath).filter(Boolean))];

/** Custom "Other" texts joined (string, so server profile moderation scans it). */
export const customTextsForPaths = paths =>
  normalizePaths(paths)
    .map(p => segs(p).find(isCustomSegment))
    .filter(Boolean)
    .map(s => s.slice(1))
    .join(', ');

export const parsePathsParam = value =>
  normalizePaths(typeof value === 'string' ? value.split('|') : value);
export const pathsParam = paths => normalizePaths(paths).join('|');

/** Is this node featured (home shortcuts, SEO)? Grief/caregiving never are. */
export const isFeaturedPath = path => {
  const n = nodeFor(path);
  return !!n && !n.unfeatured && !(n.ancestors || []).some(a => nodeFor(a)?.unfeatured);
};

/** Carry the drill-down choice into booking (prefilled, still changeable). */
const CARRY_KEY = 'sl.bookingTopicPath';
export const rememberTopicPath = path => {
  try {
    const p = normalizePath(path);
    if (p && typeof window !== 'undefined') window.sessionStorage.setItem(CARRY_KEY, p);
  } catch (e) {
    // storage blocked: ignore
  }
};
export const recalledTopicPath = (consume = false) => {
  try {
    if (typeof window === 'undefined') return null;
    const p = normalizePath(window.sessionStorage.getItem(CARRY_KEY));
    if (consume) window.sessionStorage.removeItem(CARRY_KEY);
    return p;
  } catch (e) {
    return null;
  }
};

/** Browse drill-down: listener has a path at this level or below (strict, no broader ancestors). */
export const listingAtOrBelow = (listing, at) => {
  const p = normalizePath(at);
  if (!p) return true;
  const targets = [p, ALIASES[p]].filter(Boolean);
  return pathsFromListing(listing).some(m => targets.some(t => m === t || m.startsWith(t + '/')));
};

/** Word-start match so 'ai' finds 'AI' but not 'said' or 'retail'. */
export const wordStartsIn = (hay, w) =>
  new RegExp(`(^|[^a-z0-9])${String(w).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(hay);

/** Browse keyword search over name, about, teams, shows and topic text. */
export const listingMatchesKeywords = (listing, keywords) => {
  const words = String(keywords || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return true;
  const a = listing?.attributes || {};
  const pd = a.publicData || {};
  const author = listing?.author?.attributes?.profile || {};
  const hay = [
    a.title, a.description, author.displayName,
    pd.favoriteTeams, pd.favoriteShows, pd.shows, pd.topicPathsOther, pd.topicsOther,
    ...(Array.isArray(pd.topics) ? pd.topics.map(t => String(t).replace(/-/g, ' ')) : []),
    ...pathsFromListing(listing).map(p => pathLabel(p)),
    ...pathsFromListing(listing).flatMap(p => {
      const parts = p.split('/');
      return parts.flatMap((_, i) => nodeFor(parts.slice(0, i + 1).join('/'))?.keywords || []);
    }),
  ].filter(x => typeof x === 'string').join(' ').toLowerCase();
  return words.every(w => wordStartsIn(hay, w));
};

/**
 * Talker matching on the topic tree. Score = depth of the shared branch (deeper = closer match),
 * summed over the talker's paths. Falls back to the old flat-topic ranking when no paths are saved.
 * `shared` holds the talker paths that matched (labels resolved by MatchRow).
 */
export const rankListingsByPaths = (listings = [], talkerPaths, presenceMap = {}, limit = 6, fallback) => {
  const mine = normalizePaths(talkerPaths);
  if (!mine.length) return typeof fallback === 'function' ? fallback() : [];
  const depth = (a, b) => {
    const x = a.split('/'), y = b.split('/');
    let i = 0;
    while (i < x.length && i < y.length && x[i] === y[i]) i++;
    return i;
  };
  return listings
    .map((listing, index) => {
      const theirs = pathsFromListing(listing);
      let score = 0;
      const shared = [];
      mine.forEach(t => {
        const best = theirs.reduce((m, p) => (pathsRelated(t, p) ? Math.max(m, depth(t, p)) : m), 0);
        if (best) {
          score += best;
          shared.push(t);
        }
      });
      const id = listing?.id?.uuid;
      return { listing, score, shared, online: !!(id && presenceMap[id]), index };
    })
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score || Number(b.online) - Number(a.online) || a.index - b.index)
    .slice(0, limit);
};
